import { useCallback, useMemo } from 'react'

import SpecialStatusAlert from '@helpscout/hsds-icons/special-status-alert'
import SpecialStatusCheck from '@helpscout/hsds-icons/special-status-check'

import {
  STATUS_ERROR,
  STATUS_MESSAGE,
  STATUS_SUCCESS,
  STATUS_WARNING,
} from '../Noty.constants'
import {
  ADD,
  HIDE_CONFIRM,
  REMOVE,
  REMOVE_ALL,
  SHOW_CONFIRM,
  useNotyReducer,
} from './useNotyReducer'

/**
 * @typedef {import('../Noty.constants').TNotyItem} TNotyItem
 * @typedef {import('../Noty.constants').TNotyConfirm} TNotyConfirm
 */

/**
 * @typedef TConfirmOpts
 * @property {function} onConfirm
 * @property {function} onClose
 * @property {boolean} [danger]
 * @property {string} [primaryButtonText]
 * @property {string} [secondaryButtonText]
 * @property {any} [body]
 * @property {number} [modalWidth]
 * @property {number} [modalHeight]
 * @property {boolean} [closeOnConfirm]
 * @property {boolean} [fixedPosition]
 */

/**
 * @typedef TNotyOpts
 * @property {number} [timeout]
 * @property {React.ElementType} [icon]
 * @property {TNotyPrimaryButtonOpts} [primaryButtonProps]
 */

/**
 * @typedef TNotyPrimaryButtonOpts
 * @property {string} label
 * @property {function=} onClick
 * Optional: a noty action can be a button (onClick) or a link (href). Noty
 * renders whichever is supplied — see the two primaryButtonProps cases in
 * Noty.test.js.
 * @property {string=} href
 * @property {string=} rel
 * @property {string=} target
 */

/**
 * @callback actionCallback
 * @param {string|ReactElement} msg
 * @param {TNotyOpts} [opts]
 */

/**
 * @callback confirmCallback
 * @param {string} msg
 * @param {TConfirmOpts} opts
 */

/**
 *
 * @callback hideCallback
 * @param {number} id
 */

/**
 * @typedef TNotyContext
 * @property {function} hideAllNotys
 * @property {function} hideConfirmNoty
 * @property {hideCallback} hideNoty
 * @property {confirmCallback} showConfirmNoty
 * @property {actionCallback} showErrorNoty
 * @property {actionCallback} showMessageNoty
 * @property {actionCallback} showSuccessNoty
 * @property {actionCallback} showWarningNoty
 *
 */

/**
 * Noty hook to initialize the reducer and create all callbacks that will be serve in `useNoty`
 * @returns {{notys: Array.<TNotyItem>, confirm: TNotyConfirm, contextValue: TNotyContext}}
 */
export const useNotyCore = () => {
  const [notys, confirm, dispatch] = useNotyReducer()
  const hideConfirmNoty = useCallback(() => {
    dispatch({
      type: HIDE_CONFIRM,
    })
  }, [dispatch])
  const hideNoty = useCallback(
    /**
     * @type {hideCallback}
     */
    id => {
      dispatch({
        type: REMOVE,
        id,
      })
    },
    [dispatch]
  )
  const showConfirmNoty = useCallback(
    /**
     * @type {confirmCallback}
     */
    (title, opts = {}) => {
      dispatch({
        ...opts,
        type: SHOW_CONFIRM,
        title,
      })

      return [hideConfirmNoty]
    },
    [dispatch, hideConfirmNoty]
  )

  const showErrorNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = +new Date()
      dispatch({
        opts: {
          timeout: 10000,
          icon: SpecialStatusAlert,
          ...opts,
        },
        type: ADD,
        status: STATUS_ERROR,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const showSuccessNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = +new Date()

      dispatch({
        opts: {
          timeout: 8000,
          icon: SpecialStatusCheck,
          ...opts,
        },
        type: ADD,
        status: STATUS_SUCCESS,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const showMessageNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = +new Date()

      dispatch({
        opts: {
          timeout: 10000,
          ...opts,
        },
        type: ADD,
        status: STATUS_MESSAGE,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const showWarningNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = +new Date()
      dispatch({
        opts: {
          ...opts,
        },
        type: ADD,
        status: STATUS_WARNING,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const hideAllNotys = useCallback(() => {
    dispatch({
      type: REMOVE_ALL,
    })
  }, [dispatch])

  /**
   *
   * @typedef TNotyContext
   * @property {function} hideAllNotys
   * @property {function} hideConfirmNoty
   * @property {function} hideNoty
   * @property {function} showConfirmNoty
   * @property {function} showErrorNoty
   * @property {function} showMessageNoty
   * @property {function} showSuccessNoty
   * @property {function} showWarningNoty
   *
   * @type {TNotyContext} contextValue
   */
  const contextValue = useMemo(() => {
    return {
      hideAllNotys,
      hideConfirmNoty,
      hideNoty,
      showConfirmNoty,
      showErrorNoty,
      showMessageNoty,
      showSuccessNoty,
      showWarningNoty,
    }
  }, [
    hideAllNotys,
    hideConfirmNoty,
    hideNoty,
    showConfirmNoty,
    showErrorNoty,
    showMessageNoty,
    showSuccessNoty,
    showWarningNoty,
  ])

  return {
    notys,
    confirm,
    contextValue,
  }
}
